import { useState, useEffect, useRef } from "react";
import { format, eachDayOfInterval, getDay, isFirstDayOfMonth, startOfMonth, subMonths, parseISO, isToday } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { DEMO_GRID_CONFIG } from "./constants";

interface DemoHabit {
  id: string;
  name: string;
  color: { filled: string; empty: string };
  completedDates: string[];
}

interface DemoCompactHabitGridProps {
  habit: DemoHabit;
}

// Use a fixed reference date for consistency with main demo
const DEMO_END_DATE = parseISO("2024-03-09");

export default function DemoCompactHabitGrid({ habit }: DemoCompactHabitGridProps) {
  const [gridData, setGridData] = useState<(Date | null)[][]>([]);
  const [months, setMonths] = useState<{name: string, index: number}[]>([]);
  const [monthsToShow, setMonthsToShow] = useState(3);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  // For demo, use a simpler fixed approach that works well in pinned habits cards
  useEffect(() => {
    // Use slightly more months for demo to look fuller, but keep it reasonable
    setMonthsToShow(isMobile ? 3 : 4);
  }, [isMobile]);
  
  // Generate the grid data
  useEffect(() => {
    const endDate = DEMO_END_DATE;
    const startDate = startOfMonth(subMonths(endDate, monthsToShow - 1));
    
    // Generate all days in the interval
    const allDates = eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
    
    // Create data structure for grid
    const monthsData: {name: string, index: number}[] = [];
    const grid: (Date | null)[][] = Array(7).fill(null).map(() => []);
    
    // Process each date and organize by day of week
    let currentMonth = "";
    let weekIndex = 0;
    
    // Fill the grid with weeks
    allDates.forEach((date) => {
      const dayOfWeek = getDay(date);
      
      if (dayOfWeek === 0 && grid[0].length > 0) {
        weekIndex++;
      }
      
      if (isFirstDayOfMonth(date)) {
        const monthName = format(date, 'MMM');
        if (monthName !== currentMonth) {
          currentMonth = monthName;
          monthsData.push({
            name: monthName,
            index: weekIndex
          });
        }
      }
      
      for (let i = 0; i < 7; i++) {
        while (grid[i].length <= weekIndex) {
          grid[i].push(null);
        }
      }
      
      grid[dayOfWeek][weekIndex] = date;
    });
    
    setGridData(grid);
    setMonths(monthsData);
  }, [monthsToShow]);

  // Check if a date is completed
  const isDateCompleted = (date: Date | null): boolean => {
    if (!date) return false;
    const dateString = format(date, 'yyyy-MM-dd');
    return habit.completedDates.includes(dateString);
  };

  // Weekday labels
  const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div className="demo-compact-habit-grid w-full overflow-hidden">
      <div className="w-full">
        {/* Month labels */}
        <div className="flex h-5 relative mb-1">
          <div className={`${isMobile ? 'w-3' : 'w-4'} mr-1.5`}></div>
          <div className="flex-1 relative">
            {months.map((month, i) => (
              <div 
                key={i} 
                className="absolute text-[11px] text-muted-foreground"
                style={{ left: `${month.index * (isMobile ? 11 : 13)}px` }}
              >
                {month.name}
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex w-full overflow-hidden">
          {/* Weekday labels */}
          <div className={`flex flex-col ${isMobile ? 'w-3' : 'w-4'} mr-1.5 flex-shrink-0`}>
            {weekdays.map((day, index) => (
              <div 
                key={index} 
                className="flex items-center justify-center text-[10px] text-muted-foreground"
                style={{ height: isMobile ? "9px" : "12px", marginBottom: "1.5px" }}
              >
                {day}
              </div>
            ))}
          </div>
          
          {/* Grid cells */}
          <div className="flex flex-col flex-1 min-w-0">
            {gridData.map((row, rowIndex) => (
                              <div 
                  key={`row-${rowIndex}`} 
                className={`flex ${isMobile ? 'gap-[1px]' : 'gap-[1.5px]'} mb-[1.5px]`} 
                style={{ height: isMobile ? "9px" : "12px" }}
                >
                {row.map((date, colIndex) => {
                  const isCompleted = isDateCompleted(date);
                  const dateStr = date ? format(date, 'MMM d') : '';
                  const completionStatus = isCompleted ? 'Completed' : 'Not completed';
                  const isTodayDate = date ? isToday(date) : false;
                  const todayBorder = isTodayDate ? "ring-1 ring-primary ring-offset-[0.5px] ring-offset-background" : "";
                  
                                      return (
                      <div
                        key={`cell-${rowIndex}-${colIndex}`}
                      className={`${isMobile ? 'w-2 h-2' : 'w-2.5 h-2.5'} rounded-[1.7px] transition-all hover:scale-150 hover:z-10 cursor-pointer ${todayBorder}`}
                        style={{ 
                          backgroundColor: isCompleted ? habit.color.filled : habit.color.empty,
                          visibility: date ? 'visible' : 'hidden'
                        }}
                        title={date ? `${dateStr} - ${completionStatus}${isTodayDate ? ' (Today)' : ''}` : ''}
                      />
                    );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
} 